import React, { useState, useEffect } from "react";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import { useUtils } from "@material-ui/pickers";
import { fade, Theme } from "@material-ui/core/styles";
import withStyles from "@material-ui/core/styles/withStyles";
import clsx from "clsx";
import { FieldRenderProps } from "react-final-form";
import MomentUtils from "@date-io/moment";
import { IconButton } from "@material-ui/core";
import ClearIcon from "@material-ui/icons/Clear";
import { observer } from "mobx-react-lite";
import moment, { Moment } from "moment";
import { LoggerFactory } from "../../../logger";
import { DateType } from "@date-io/type";

export interface DateRangePickerProps extends FieldRenderProps<any> {
  classes: any;
  clearable?: boolean;
  labelFunc?: (range: any, invalid: any) => string;
  format?: any;
  emptyLabel?: string;
  autoOk?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  open: any;
}

const DateRangePicker = observer(
  ({
    classes,
    input: { value, onChange },
    clearable = true,
    labelFunc,
    error,
    format,
    emptyLabel,
    autoOk,
    onOpen,
    onClose,
    touched,
    open: openForward,
    ...props
  }: DateRangePickerProps) => {
    const log = LoggerFactory.create(`DateRangePicker`);
    const utils = useUtils();

    const [begin, setBegin] = useState(() => {
      log.debug("value on init", value);
      return typeof value === "object" ? moment(value.begin) : value[0];
    });
    const [end, setEnd] = useState(() => {
      return typeof value === "object" ? moment(value.end) : value[1];
    });
    const [prevBegin, setPrevBegin] = useState(undefined);
    const [prevEnd, setPrevEnd] = useState(undefined);
    const [hasClicked, setHasClicked] = useState(false);
    const [hover, setHover] = useState(undefined);
    const [accepted, setAccepted] = useState(!!begin && !!end);

    const min = Math.min(begin, end || hover);
    const max = Math.max(begin, end || hover);

    const [open, setOpen] = useState(false);

    const isOpen = openForward !== undefined ? openForward : open;

    useEffect(() => {
      if (!value) {
        setPrevBegin(undefined);
        setPrevEnd(undefined);
        setBegin(undefined);
        setEnd(undefined);
      }
    }, [value]);

    useEffect(() => {
      //Only way to get to this state is is openForward is used
      if (isOpen && accepted && !prevBegin && !prevEnd) {
        setAccepted(false);
        setPrevBegin(begin);
        setPrevEnd(end);
        return;
      }
      //Closed without accepting, reset to prev state, don't find onChange
      if (!isOpen && !accepted) {
        setBegin(prevBegin);
        setEnd(prevEnd);
        setHover(undefined);
        setHasClicked(false);
      }
      //Auto ok and hasn't been accepted, but has all the items set, accept and close.
      //This will also trigger the on change event by setting isOpen to false
      if (isOpen && autoOk && !accepted && begin && end && hasClicked) {
        setAccepted(true);
        onClose ? onClose() : setOpen(false);
      }
      if (accepted && begin && end && !isOpen && hasClicked) {
        setHasClicked(false);
        onChange({
          begin,
          end: end.endOf("day")
        });
        onClose ? onClose() : setOpen(false);
      }
    }, [
      begin,
      end,
      autoOk,
      accepted,
      isOpen,
      prevBegin,
      hasClicked,
      prevEnd,
      onClose,
      onChange
    ]);

    function renderDay(
      day: DateType | null,
      selectedDate: DateType | null,
      dayInCurrentMonth: boolean,
      dayComponent: JSX.Element
    ) {
      return React.cloneElement(dayComponent, {
        onClick: (e: any) => {
          setHasClicked(true);
          e.stopPropagation();
          if (!begin) setBegin(day);
          else if (!end) {
            if (utils.isBeforeDay(day, begin)) {
              setEnd(begin);
              setBegin(day);
            } else {
              setEnd(day);
            }
            if (autoOk) {
              setPrevBegin(undefined);
              setPrevEnd(undefined);
            }
          } else {
            setBegin(day);
            setEnd(undefined);
          }
        },
        // @ts-ignore
        onMouseEnter: () => requestAnimationFrame(() => setHover(day)),
        // @ts-ignore
        onFocus: () => requestAnimationFrame(() => setHover(day)),
        className: clsx(classes.day, {
          [classes.hidden]: dayComponent.props.hidden,
          [classes.current]: dayComponent.props.current,
          [classes.isDisabled]: dayComponent.props.disabled,
          [classes.focusedRange]:
            // @ts-ignore
            (utils.isAfterDay(day, min) && utils.isBeforeDay(day, max)) ||
            // @ts-ignore
            (utils.isSameDay(day, min) && !utils.isSameDay(day, max)) ||
            // @ts-ignore
            (utils.isSameDay(day, max) && !utils.isSameDay(day, min)),
          [classes.focusedFirst]:
            // @ts-ignore
            utils.isSameDay(day, min) && !utils.isSameDay(day, max),
          [classes.focusedLast]:
            // @ts-ignore
            utils.isSameDay(day, max) && !utils.isSameDay(day, min),
          // @ts-ignore
          [classes.beginCap]: utils.isSameDay(day, min),
          // @ts-ignore
          [classes.endCap]: utils.isSameDay(day, max)
        })
      });
    }

    const formatDate = (date: any) =>
      utils.format(date, format || utils.dateFormat);

    const onClear = (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
      event.stopPropagation();
      onChange(undefined);
      setPrevBegin(undefined);
      setPrevEnd(undefined);
      setBegin(undefined);
      setEnd(undefined);
    };

    const labelingFunc = (date: Moment | null, invalid: string) => {
      if (!isOpen) {
        if (labelFunc) {
          return labelFunc([begin, end], invalid);
        } else {
          if (date && begin && end) {
            return `${formatDate(begin)} - ${formatDate(end)}`;
          } else {
            return emptyLabel || "";
          }
        }
      } else {
        if (prevBegin && prevEnd) {
          if (labelFunc) {
            return labelFunc([prevBegin, prevEnd], invalid);
          } else {
            return `${formatDate(prevBegin)} - ${formatDate(prevEnd)}`;
          }
        } else {
          return emptyLabel || "";
        }
      }
    };

    return (
      <MuiPickersUtilsProvider utils={MomentUtils}>
        <DatePicker
          value={begin}
          error={error && touched}
          inputVariant={"outlined"}
          InputProps={{
            endAdornment: (
              <>
                {clearable && (
                  <IconButton onClick={event => onClear(event)} size={"small"}>
                    <ClearIcon />
                  </IconButton>
                )}
              </>
            )
          }}
          margin="dense"
          fullWidth={true}
          renderDay={renderDay}
          open={isOpen}
          onOpen={() => {
            setAccepted(false);
            setPrevBegin(begin);
            setPrevEnd(end);
            onOpen ? onOpen() : setOpen(true);
          }}
          onAccept={() => {
            if (!begin || !end) {
              // @ts-ignore
              if (hover && utils.isBeforeDay(begin, hover)) {
                setEnd(hover);
              } else {
                setEnd(begin);
                setBegin(hover);
              }
            }
            setPrevBegin(undefined);
            setPrevEnd(undefined);
            // if (!autoOk) {
            setAccepted(true);
            // }
          }}
          onClose={() => {
            onClose ? onClose() : setOpen(false);
          }}
          onChange={() => {}}
          labelFunc={labelingFunc}
          DialogProps={{ className: classes.dateRangePickerDialog }}
        />
      </MuiPickersUtilsProvider>
    );
  }
);

const styles = (theme: Theme) => {
  const focusedRangeColor = fade(theme.palette.primary.main, 0.3);
  const focusedRangeGradient = `linear-gradient(to right, ${focusedRangeColor}, ${focusedRangeColor})`;
  const transparentRangeGradient = `linear-gradient(to right, rgba(0,0,0,0.0), rgba(0,0,0,0.0))`;
  return {
    dateRangePickerDialog: {
      "& .MuiPickersCalendar-transitionContainer": {
        minHeight: 218,
        marginTop: 10
      }
    },
    day: {
      width: 40,
      height: 36,
      fontSize: theme.typography.caption.fontSize,
      margin: 0,
      color: theme.palette.text.primary,
      fontWeight: theme.typography.fontWeightMedium,
      padding: 0,
      transition: "none",
      "&::after": {
        borderRadius: "100%",
        bottom: 0,
        boxSizing: "border-box",
        content: '""',
        height: 36,
        width: 36,
        left: 0,
        margin: "auto",
        position: "absolute",
        right: 0,
        top: 0,
        transform: "scale(0)",
        zIndex: 2
      },
      "&:hover": {
        backgroundColor: "transparent",
        color: theme.palette.text.primary,
        "&::after": {
          backgroundColor: theme.palette.background.paper,
          border: `2px solid ${theme.palette.primary.main}`,
          bottom: -2,
          left: -2,
          height: 36,
          width: 36,
          right: -2,
          top: -2,
          boxSizing: "content-box",
          transform: "scale(1)"
        }
      },
      "& > .MuiIconButton-label": {
        zIndex: 3
      }
    },
    hidden: {
      opacity: 0,
      pointerEvents: "none"
    },
    current: {
      color: theme.palette.primary.main,
      fontWeight: 600
    },
    focusedRange: {
      color: theme.palette.primary.contrastText,
      background: `${focusedRangeGradient} no-repeat 0/20px 40px, ${focusedRangeGradient} no-repeat 20px 0/20px 40px`,
      fontWeight: theme.typography.fontWeightMedium,
      width: 40,
      marginRight: 0,
      marginLeft: 0,
      borderRadius: 0
    },
    dayDisabled: {
      pointerEvents: "none",
      color: theme.palette.text.hint
    },
    beginCap: {
      "&::after": {
        transform: "scale(1)",
        backgroundColor: theme.palette.primary.main
      }
    },
    endCap: {
      "&::after": {
        transform: "scale(1)",
        backgroundColor: theme.palette.primary.main
      }
    },
    focusedFirst: {
      background: `${transparentRangeGradient} no-repeat 0/20px 40px,${focusedRangeGradient} no-repeat 20px 0/20px 40px`
    },
    focusedLast: {
      background: `${focusedRangeGradient} no-repeat 0/20px 40px,${transparentRangeGradient} no-repeat 20px 0/20px 40px`
    }
  };
};

// @ts-ignore
export default withStyles(styles, { name: "DateRangePicker" })(DateRangePicker);
